import React from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'react-bootstrap';

import SectionModal from 'components/plugin/section_modal';
import WebsiteSectionList from 'components/plugin/website_section_list';
import WebsiteSectionApi from 'components/plugin/website_section_api';

import './website_section_editor.scss';
import { debounce, findIndex, flatten, groupBy, pull, remove } from 'lodash';

export default class WebsiteSectionEditor extends React.Component {
  static propTypes = {
    containerId: PropTypes.string.isRequired,
    customTables: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    layout: PropTypes.string,
    paths: PropTypes.shape({
      sections: PropTypes.string.required
    }).isRequired,
    saleOrLease: PropTypes.string,
    sections: PropTypes.array,
    sectionTypes: PropTypes.array.isRequired
  };

  static defaultProps = {
    disabled: false
  };

  api = new WebsiteSectionApi(this.props.paths);
  state = {
    currentSection: {},
    layout: this.props.layout,
    modalShown: false,
    sections: this.props.sections || []
  };
  targetGroup = Math.random().toString(36).substring(2);

  processLayoutChanged = (e) => {
    this.setState({ layout: e.detail.template });
  }

  componentDidMount() {
    document.getElementById(this.props.containerId).addEventListener(
      'layoutChanged', this.processLayoutChanged, false
    );
  }

  componentWillUnmount() {
    document.getElementById(this.props.containerId).removeEventListener(
      'layoutChanged', this.processLayoutChanged, false
    );
  }

  handleAdd = () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({
      modalShown: true,
      currentSection: {
        location: 'main',
        saleOrLease: this.props.saleOrLease,
        sectionType: this.props.sectionTypes[0].key
      }
    });
  };

  handleEdit = (sectionToEdit) => {
    if (this.props.disabled) {
      return;
    }
    this.setState({
      modalShown: true,
      currentSection: sectionToEdit
    });
  };

  handleModalSuccess = (section) => {
    if (this.props.disabled) {
      return;
    }

    if (section.id == null) {
      if (section.sectionType == 'custom_button') {
        section.location = 'sidebar';
      }
      this.addSection(section);
    } else {
      this.updateSection(section);
    }
  };

  addSection = (sectionToAdd) => {
    if (this.props.disabled) {
      return;
    }

    this.api.createSection(sectionToAdd, (section) => {
      this.setState((prevState) => {
        const sections = prevState.sections;
        sections.push(section);
        return {
          sections: sections,
          modalShown: false
        };
      });
    });
  };

  updateSection = (sectionToUpdate) => {
    if (this.props.disabled) {
      return;
    }

    this.api.updateSection(sectionToUpdate, (updatedSection) => {
      this.setState((prevState) => {
        const sections = [...prevState.sections];
        const sectionIndex = findIndex(sections, section => section.id === updatedSection.id);
        sections[sectionIndex] = updatedSection;

        return {
          sections: sections,
          modalShown: false
        };
      });
    });
  };

  handleDelete = (sectionToDelete) => {
    if (this.props.disabled) {
      return;
    }

    this.api.deleteSection(sectionToDelete, () => {
      this.setState((prevState) => {
        const sections = prevState.sections;
        remove(sections, section => section.id === sectionToDelete.id);
        return { sections: sections };
      });
    });
  };

  handleReorder = (index1, index2, list1, list2) => {
    if (this.props.disabled || !list1 || !list2) {
      return;
    }

    let sections = this.sectionsByLocation(this.state.sections);
    if (!sections[list1]) {
      // Fixes occasional movement bug
      return;
    }

    let section;

    if (list1 === list2) {
      const list = sections[list1];
      section = list[index1];
      if (list[index2]) {
        list[index1] = list[index2];
        list[index2] = section;
      } else {
        list.push(list.splice(index1, 1)[0]);
      }
    } else {
      section = sections[list1][index1];
      section.location = list2;
      pull(sections[list1], section);
      sections[list2] = sections[list2] || [];
      if (index2 < 0) {
        sections[list2].push(section);
      } else {
        sections[list2].splice(index2, 0, section);
      }
    }

    this.refreshOrder(sections);
    sections = flatten(Object.values(sections));
    this.setState(() => {
      this.apiReorder(sections);
      return { sections: sections };
    });
  };

  apiReorder = debounce(sections => this.api.reorderSections(sections), 250);

  refreshOrder(sections) {
    let location;
    for (location in sections) {
      sections[location].forEach((section, i) => {
        section.sortOrder = i + 1;
      });
    }
  }

  handleModalHide = () => {
    this.setState({ modalShown: false });
  };

  sectionsByLocation = (sections) => {
    return groupBy(sections, 'location');
  };

  usingDefaultLayout = () => {
    return this.state.sections.length === 0;
  };

  render() {
    return (
      <React.Fragment>
        <Row>
          {this.renderSectionList("Main Section", "main", 7)}
          {this.state.layout !== 'v3/landing_page' && this.renderSectionList("Sidebar", "sidebar", 5)}
        </Row>
        {this.renderButtonRow()}
        {this.renderModal()}
      </React.Fragment>
    );
  }

  renderButtonRow() {
    if (this.props.disabled) {
      return;
    }

    return (
      <Row>
        <Col md={12}>
          <Button bsClass="btn btn-create" name="new-section-button" onClick={this.handleAdd}>
            + New Section
          </Button>
        </Col>
      </Row>
    );
  }

  renderSectionList(title, location, columnSize) {
    const sections = this.sectionsByLocation(this.state.sections)[location] || [];
    return <Col md={columnSize}>
      <h4 className="section-location-header">{title}</h4>
      <WebsiteSectionList
        defaultLayout={this.usingDefaultLayout()}
        disabled={this.props.disabled}
        layout={this.state.layout}
        location={location}
        name={`${location}-section-list`}
        sections={sections}
        sectionTypes={this.props.sectionTypes}
        targetGroup={this.targetGroup}
        onDeleteSection={this.handleDelete}
        onEditSection={this.handleEdit}
        onReorder={this.handleReorder}
      />
    </Col>;
  }

  renderModal() {
    if (!this.props.disabled) {
      return (
        <SectionModal
          customTables={this.props.customTables}
          layout={this.state.layout}
          section={this.state.currentSection}
          sectionTypes={this.props.sectionTypes}
          shown={this.state.modalShown}
          onHide={this.handleModalHide}
          onSuccess={this.handleModalSuccess}
        />
      );
    }
  }
}
